import { ColumnSortableButton, ColumnSortableIcon } from '@/components/table/column-sortable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Time } from '@/components/ui/time';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EnumAlertStatus, MonitoringAlertSearchDto } from '@/lib/api/types.gen';
import { RoutePath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { ColumnDef, RowData } from '@tanstack/react-table';
import { ArrowUpDown, Circle, CircleCheck, CircleDashed, Dot, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'right' | 'center';
    style?: React.CSSProperties;
    label?: string;
  }
}

const statusConfig = {
  New: { color: 'bg-blue-600', name: 'New' },
  Weather: { color: 'bg-cyan-700', name: 'Weather' },
  Other: { color: 'bg-gray-900', name: 'Other' },
  FalsePositive: { color: 'bg-gray-900', name: 'False Alarm' },
  PossiblyStaff: { color: 'bg-gray-900', name: 'Possibly Staff' },
  ConfirmedStaff: { color: 'bg-gray-900', name: 'Confirmed Staff' },
  Intruder: { color: 'bg-red-800', name: 'Possibly Intruder' },
  Unknown: { color: 'bg-gray-900', name: 'Unknown' },
  NoThreat: { color: 'bg-gray-900', name: 'No Threat' },
  SuspiciousPerson: { color: 'bg-red-800', name: 'Suspicious Person' },
  AnimalOrInsect: { color: 'bg-gray-900', name: 'Animal or Insect' },
  Environment: { color: 'bg-gray-900', name: 'Environment' },
  LightReflection: { color: 'bg-gray-900', name: 'Light Reflection' },
  NeedsService: { color: 'bg-gray-900', name: 'Needs Service' },
  IncorrectMotionGrids: { color: 'bg-gray-900', name: 'Incorrect Motion Grids' },
};

function formatSecondsToHMS(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(hours > 0 ? 2 : 0, '0'),
    seconds.toString().padStart(minutes > 0 ? 2 : 0, '0'),
  ]
    .join(':')
    .replace(/00:/g, '');
}

export type AlertDataTableData = {
  id?: string;
  alertId?: string;
  customerName?: string;
  customerId?: string;
  siteName?: string;
  siteId?: string;
  status?: EnumAlertStatus;
  occurred?: Date;
  closedAt?: Date;
  actionedAt?: Date;
  actionedBy?: string;
  slaBreach?: boolean;
  slaDue?: Date;
};

export const extractAlertTableData = (alerts: MonitoringAlertSearchDto[]): AlertDataTableData[] => {
  return alerts.map((a) => {
    return {
      id: a.id,
      alertId: a.alertId ?? undefined,
      actionedAt: a.actionedAt ? new Date(a.actionedAt) : undefined,
      closedAt: a.closedAt ? new Date(a.closedAt) : undefined,
      customerName: a.companyName ?? undefined,
      customerId: a.companyId,
      siteName: a.projectName ?? undefined,
      siteId: a.projectId,
      occurred: a.occurred ? new Date(a.occurred) : undefined,
      slaBreach: a.hadSlaBreach,
      status: a.status,
      actionedBy: a.actionedBy ?? undefined,
      slaDue: a.nextActionSlaDue ? new Date(a.nextActionSlaDue) : undefined,
    };
  });
};

export const alertDataTableColumns: ColumnDef<AlertDataTableData>[] = [
  {
    enableHiding: false,
    id: 'state',
    header: 'State',
    meta: {
      align: 'center',
    },
    cell({ row }) {
      const { closedAt, slaBreach } = row.original;

      if (closedAt == null) {
        return (
          <Tooltip>
            <TooltipTrigger>
              <CircleDashed
                className={cn('h-4 w-4 rounded-full bg-gray-800/50 text-gray-300', {
                  'bg-orange-800/50 text-orange-500': slaBreach,
                })}
              />
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12}>
              {slaBreach ? 'Alert is open - SLA breached' : 'Alert is open'}
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger>
            <Circle
              className={cn('h-4 w-4 rounded-full bg-emerald-800 text-emerald-300', {
                'bg-orange-800 text-orange-500': slaBreach,
              })}
            />
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            {slaBreach ? 'Alert closed - SLA breached' : 'Alert closed'}
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    enableHiding: false,
    id: 'alertId',
    meta: { label: 'Alert ID', align: 'left' },
    accessorKey: 'alertId',
    header: 'Alert ID',
    cell({ row }) {
      const { alertId, id } = row.original;

      if (!alertId) return null;

      return (
        <Link className="font-semibold text-lime" to={`/monitoring/alert/${id}`}>
          {alertId}
        </Link>
      );
    },
  },
  {
    id: 'customer',
    accessorKey: 'customerName',
    meta: { label: 'Customer Name' },
    header: 'Customer',
    cell({ row }) {
      const { customerName, customerId } = row.original;

      return (
        <Link to={RoutePath.Customer(customerId ?? '')}>
          <span className="font-bold">{customerName}</span>
        </Link>
      );
    },
  },
  {
    id: 'Site Name',
    accessorKey: 'siteName',
    header: 'Site',
    meta: { label: 'Site' },
    cell({ row }) {
      const { siteName, customerId, siteId } = row.original;

      return <Link to={RoutePath.CustomerSite(customerId ?? '', siteId ?? '')}>{siteName}</Link>;
    },
  },
  {
    id: 'occurred',
    accessorKey: 'occurred',
    meta: { label: 'Occurred' },
    header: 'Occurred',
    cell({ row }) {
      const occurred = row.original.occurred;
      if (!occurred) return null;
      return <span className="opacity-60"><Time date={occurred} /></span>;
    },
  },
  {
    id: 'closedAt',
    accessorKey: 'closedAt',
    meta: {
      label: 'Closed At',
    },
    header: 'Closed At',
    cell({ row }) {
      const { closedAt } = row.original;

      return <span className="opacity-50">{closedAt ? <Time date={closedAt} /> : '-'}</span>;
    },
  },
  {
    id: 'actionedAt',
    accessorKey: 'actionedAt',
    meta: {
      label: 'Actioned At',
    },
    header: 'Actioned At',
    cell({ row }) {
      const { actionedAt } = row.original;
      return <span className="opacity-50">{actionedAt ? <Time date={actionedAt} /> : '-'}</span>;
    },
  },
  {
    id: 'actionedBy',
    accessorKey: 'actionedBy',
    meta: {
      label: 'Actioned By',
    },
    header: 'Actioned By',
    cell({ row }) {
      const { actionedBy } = row.original;
      return <span className="opacity-50">{actionedBy}</span>;
    },
  },
  {
    id: 'slaDue',
    accessorKey: 'slaDue',
    meta: {
      label: 'SLA Due',
      align: 'right',
    },
    header: 'SLA Due',
    cell({ row }) {
      const actionedAt = row.original.actionedAt;
      return <span className="opacity-50">{actionedAt ? <Time date={actionedAt} /> : '-'}</span>;
    },
  },
  {
    id: 'duration',
    header: 'Duration',
    meta: { label: 'Duration' },
    cell({ row }) {
      const { occurred, closedAt } = row.original;

      if (occurred == null || closedAt == null) return null;

      const totalSeconds = Math.floor((closedAt.getTime() - occurred.getTime()) / 1000);

      return (
        <div className="flex items-center gap-x-1 opacity-80">
          <Timer className="h-3 w-3" />
          <p className="text-xs font-medium">{formatSecondsToHMS(totalSeconds)}</p>
        </div>
      );
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    meta: {
      label: 'Status',
      align: 'right',
    },
    cell({ row }) {
      const status = row.original.status;

      if (!status) return null;

      const config = statusConfig[status as keyof typeof statusConfig] || {
        color: 'bg-gray-500',
        name: status,
      };

      return (
        <Badge className={`${config.color} text-white hover:opacity-80`}> {config.name} </Badge>
      );
    },
  },
];
